import React, { useState } from 'react';
import Header from '../common/Header';
import { getApiUrl } from '../../utils/apiConfig';
import { supabase } from '../../utils/supabase';

const colorPalette = {
  textDefault: '#000000',
  borderDefault: '#D9D9D9',
  borderBrand: '#000000',
  c7ab7d: '#C7AB7D',
  f0f9d6: '#F0F9D6',
  black: '#000000',
  greenDark: '#2C5057',
  greenMid: '#557257',
  white: '#FFFFFF',
};

const TeacherProfileForm = () => {
  const [formData, setFormData] = useState({
    school: '',
    position: '',
    teachingSubject: '',
    spedCertified: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent duplicate submissions
    if (loading || submitted) {
      console.log('⚠️ Form submission already in progress or completed, ignoring duplicate click');
      return;
    }

    setLoading(true);
    setError('');

    // Simple validation
    if (
      !formData.school ||
      !formData.position ||
      !formData.teachingSubject
    ) {
      setError('Please fill out all required fields.');
      setLoading(false);
      return;
    }

    try {
      // Get the user_id from Supabase session (user is logged in)
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      let userId, username;
      
      if (sessionError || !session?.user?.id) {
        // Fall back to localStorage (for signup flow)
        const signupUserId = localStorage.getItem('signupUserId');
        if (!signupUserId) {
          throw new Error('No user ID found. Please log in or complete signup first.');
        }
        userId = signupUserId;
        username = localStorage.getItem('signupUsername') || 'Unknown';
        console.log('🔑 Using user_id from localStorage (signup flow):', userId);
      } else {
        // User is logged in, use Supabase user ID
        userId = session.user.id;
        const userMetadata = session.user.user_metadata || {};
        username = userMetadata.username || localStorage.getItem('signupUsername') || session.user.email?.split('@')[0] || 'Unknown';
        console.log('🔑 Using user_id from Supabase session:', userId);
      }
      
      console.log('👤 Using username:', username);

      // Send teacher profile data to backend
      console.log('🔄 Sending teacher profile data to backend...');
      const response = await fetch(getApiUrl('teacher-profiles'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          schoolName: formData.school,
          UserName: username,
          role: formData.position,
          subject: formData.teachingSubject,
          comments: formData.spedCertified ? 'SPED Certified' : 'Not SPED Certified'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 409) {
          // Profile already exists, continue anyway
          console.log('⚠️ Teacher profile already exists, continuing to teacher dashboard...');
          setSubmitted(true);
          localStorage.removeItem('signupUserId');
          localStorage.removeItem('signupUsername');
          window.location.href = '/teacher';
          return;
        }
        throw new Error(errorData.error || 'Failed to save teacher profile');
      }

      const result = await response.json();
      console.log('✅ Teacher profile saved to backend:', result);

      // Save profile data to localStorage for immediate access
      // Reuse the session we already have, or get it if we used localStorage fallback
      let currentSession = session;
      if (!currentSession) {
        const { data: { session: newSession } } = await supabase.auth.getSession();
        currentSession = newSession;
      }
      const userMetadata = currentSession?.user?.user_metadata || {};
      const profileDataForStorage = {
        userId: userId,
        fullName: userMetadata.name || localStorage.getItem('signupName') || '',
        email: currentSession?.user?.email || localStorage.getItem('signupEmail') || '',
        username: username,
        schoolName: formData.school,
        role: formData.position,
        subject: formData.teachingSubject,
        comments: formData.spedCertified ? 'SPED Certified' : 'Not SPED Certified'
      };
      localStorage.setItem('teacherProfileData', JSON.stringify(profileDataForStorage));
      console.log('💾 Saved teacher profile to localStorage:', profileDataForStorage);

      // Mark as submitted to prevent duplicate submissions
      setSubmitted(true);

      // Clear the stored user_id and username to prevent reuse (if they exist)
      localStorage.removeItem('signupUserId');
      localStorage.removeItem('signupUsername');
      console.log('🧹 Cleared signupUserId and signupUsername from localStorage');

      // Redirect to teacher dashboard
      console.log('🎯 Redirecting to teacher dashboard...');
      window.location.href = '/teacher';

    } catch (error) {
      console.error('Error saving teacher profile:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      height: '100vh',
      background: 'linear-gradient(270deg, #44746D 5%, #283D40 97%)',
      fontFamily: 'Arial, sans-serif',
      color: colorPalette.textDefault,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Header />
      {/* Main Content */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '40px',
        maxWidth: '1600px',
        margin: '0 auto',
        width: '100%',
        overflow: 'auto'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '80px' }}>
          <h1 style={{
            color: colorPalette.c7ab7d,
            fontSize: '40px',
            fontWeight: '700',
            marginBottom: '20px'
          }}>
            Welcome Teacher!
          </h1>
          <p style={{
            color: colorPalette.c7ab7d,
            fontSize: '25px',
            lineHeight: '1.5',
            maxWidth: '900px',
            margin: '0 auto',
            fontWeight: 500
          }}>
            Thank you for trusting us to empower your student.<br/>
            Please fill out your profile form below. And list students you would like to enroll in this platform.
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '60px',
          alignItems: 'start',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {/* Left Column */}
          <div style={{ marginLeft: '-60px' }}>
            {error && (
              <div style={{
                background: 'rgba(220, 38, 38, 0.9)',
                color: colorPalette.white,
                padding: '12px 16px',
                borderRadius: '8px',
                fontSize: '16px',
                marginBottom: '20px',
                textAlign: 'center'
              }}>{error}</div>
            )}
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                color: colorPalette.c7ab7d,
                fontSize: '20px',
                fontWeight: '700',
                marginBottom: '10px'
              }}>Organization employed by</label>
              <input
                type="text"
                name="school"
                value={formData.school}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '14px 18px',
                  border: `2px solid ${colorPalette.borderDefault}`,
                  borderRadius: '8px',
                  fontSize: '18px',
                  background: colorPalette.f0f9d6,
                  color: colorPalette.textDefault,
                  fontWeight: 500
                }}
              />
            </div>
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                color: colorPalette.c7ab7d,
                fontSize: '20px',
                fontWeight: '700',
                marginBottom: '10px'
              }}>Role - Position</label>
              <input
                type="text"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '14px 18px',
                  border: `2px solid ${colorPalette.borderDefault}`,
                  borderRadius: '8px',
                  fontSize: '18px',
                  background: colorPalette.f0f9d6,
                  color: colorPalette.textDefault,
                  fontWeight: 500
                }}
              />
            </div>
          </div>

          {/* Right Column */}
          <div>
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                color: colorPalette.c7ab7d,
                fontSize: '20px',
                fontWeight: '700',
                marginBottom: '10px'
              }}>Teaching Subject</label>
              <input
                type="text"
                name="teachingSubject"
                value={formData.teachingSubject}
                onChange={handleInputChange}
                required
                style={{
                  width: '600px',
                  padding: '14px 18px',
                  border: `2px solid ${colorPalette.borderDefault}`,
                  borderRadius: '8px',
                  fontSize: '18px',
                  background: colorPalette.f0f9d6,
                  color: colorPalette.textDefault,
                  fontWeight: 500
                }}
              />
            </div>
            <div style={{ marginBottom: '30px' }}>
              <label style={{
                display: 'block',
                color: colorPalette.c7ab7d,
                fontSize: '20px',
                fontWeight: '700',
                marginBottom: '10px'
              }}>Comments your would like to share:</label>
              <input
                type="text"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '14px 18px',
                  border: `2px solid ${colorPalette.borderDefault}`,
                  borderRadius: '8px',
                  fontSize: '18px',
                  background: colorPalette.f0f9d6,
                  color: colorPalette.textDefault,
                  fontWeight: 500
                }}
              />
            </div>
            <div style={{ marginTop: '30px' }}>
              <button
                type="submit"
                className="app-btn"
                onClick={handleSubmit}
                disabled={loading || submitted}
                style={{
                  background: colorPalette.greenMid,
                  color: colorPalette.c7ab7d,
                  border: 'default',
                  padding: '14px 48px',
                  borderRadius: '10px',
                  fontSize: '22px',
                  fontWeight: '700',
                  cursor: (loading || submitted) ? 'not-allowed' : 'pointer',
                  opacity: (loading || submitted) ? 0.6 : 1,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                }}
              >
                {submitted ? 'Profile Submitted!' : loading ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeacherProfileForm;